import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared autosave for forms/tables.
 *
 * - schedule(key, patch): accumulates patches per key and PATCHes after `delay`ms idle.
 * - flushAll(): fires any pending saves immediately (await'able).
 * - status: "idle" | "saving" | "saved" | "error".
 *
 * Flushes automatically on: component unmount, tab hidden (visibilitychange),
 * page unload (pagehide + beforeunload). This fixes "lost edits when switching
 * tabs or navigating away before the debounce ticked".
 *
 * `saveFn(key, patch)` is called once per key.
 */
export default function useAutosave(saveFn, { delay = 600 } = {}) {
  const [status, setStatus] = useState("idle");
  const [savingKeys, setSavingKeys] = useState({});
  const pendingRef = useRef({}); // {key: mergedPatch}
  const timersRef = useRef({});  // {key: timeoutId}
  const inflightRef = useRef(0);
  const savedTimerRef = useRef(null);
  // Keep the latest saveFn in a ref so the callbacks below stay stable across renders
  const saveRef = useRef(saveFn);
  saveRef.current = saveFn;

  const flushKey = useCallback(async (key) => {
    const patch = pendingRef.current[key];
    if (!patch) return;
    delete pendingRef.current[key];
    if (timersRef.current[key]) { clearTimeout(timersRef.current[key]); delete timersRef.current[key]; }
    inflightRef.current += 1;
    setStatus("saving");
    try {
      await saveRef.current(key, patch);
      setSavingKeys(s => { const n = { ...s }; delete n[key]; return n; });
    } catch (e) {
      setStatus("error");
      setSavingKeys(s => ({ ...s, [key]: "error" }));
      return;
    } finally {
      inflightRef.current -= 1;
    }
    if (inflightRef.current === 0 && Object.keys(pendingRef.current).length === 0) {
      setStatus("saved");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setStatus(s => s === "saved" ? "idle" : s), 1500);
    }
  }, []);

  const flushAll = useCallback(async () => {
    const keys = Object.keys(pendingRef.current);
    await Promise.all(keys.map(flushKey));
  }, [flushKey]);

  const schedule = useCallback((key, patch) => {
    pendingRef.current[key] = { ...(pendingRef.current[key] || {}), ...patch };
    setSavingKeys(s => ({ ...s, [key]: "pending" }));
    setStatus("saving");
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(() => flushKey(key), delay);
  }, [delay, flushKey]);

  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === "hidden") flushAll(); };
    const onPageHide = () => flushAll();
    const onBeforeUnload = (e) => {
      if (Object.keys(pendingRef.current).length === 0) return;
      flushAll();
      // Warn only if saves have failed. Browsers will show a generic prompt.
      e.preventDefault(); e.returnValue = "";
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      flushAll();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [flushAll]);

  return { status, savingKeys, schedule, flushKey, flushAll };
}
